import { useCallback, useEffect, useRef, useState } from 'react';

import { type DemoPoint } from './jsm-playground-demo-geometry';
import { usePrefersReducedMotion } from './jsm-playground-motion';

export type DemoScheduler = (ms: number, run: () => void) => void;

export const useDemoTimeline = (
	isEnabled: boolean,
	hasUserTakenOver: boolean,
	schedule: (at: DemoScheduler) => void,
) => {
	const prefersReducedMotion = usePrefersReducedMotion();
	const hasUserTakenOverRef = useRef(hasUserTakenOver);
	hasUserTakenOverRef.current = hasUserTakenOver;
	const scheduleRef = useRef(schedule);
	scheduleRef.current = schedule;
	const shouldPlay = isEnabled && !prefersReducedMotion;

	useEffect(() => {
		if (!shouldPlay) {
			return;
		}

		const timers: number[] = [];
		scheduleRef.current((ms, run) => {
			timers.push(
				window.setTimeout(() => {
					if (!hasUserTakenOverRef.current) {
						run();
					}
				}, ms),
			);
		});

		return () => timers.forEach(window.clearTimeout);
	}, [shouldPlay]);
};

type DemoCursorState<Target extends string> = {
	target: Target;
	isVisible: boolean;
	isPressed: boolean;
};

const ORIGIN: DemoPoint = { x: 0, y: 0 };

export const useDemoCursor = <Target extends string>(initialTarget: Target) => {
	const [cursor, setCursor] = useState<DemoCursorState<Target>>({
		target: initialTarget,
		isVisible: false,
		isPressed: false,
	});
	const [stops, setStops] = useState<Partial<Record<Target, DemoPoint>>>({});
	const [isTransitionEnabled, setIsTransitionEnabled] = useState(false);

	useEffect(() => {
		setIsTransitionEnabled(true);
	}, []);

	const updateCursor = useCallback(
		(next: Partial<DemoCursorState<Target>>) => setCursor((previous) => ({ ...previous, ...next })),
		[],
	);

	const updateStops = useCallback(
		(next: Partial<Record<Target, DemoPoint>>) =>
			setStops((previous) => ({ ...previous, ...next })),
		[],
	);

	return {
		isVisible: cursor.isVisible,
		isPressed: cursor.isPressed,
		position: stops[cursor.target] ?? ORIGIN,
		isTransitionEnabled,
		setIsTransitionEnabled,
		updateCursor,
		updateStops,
	};
};
